/**
 * Fits a territory name into the fixed name band at the bottom of a card.
 *
 * The band never grows, so the art and title above it stay aligned across a
 * hand. A name wraps to two lines at the base size; if it still does not fit,
 * the size steps down to a readable floor, and only then is the second line
 * ellipsized. The measurement is a pure function over Zilla Slab Italic's
 * advance widths (react-native-web ignores adjustsFontSizeToFit), so iOS,
 * Android and web produce the same result.
 */

/**
 * ZillaSlab-Italic.ttf advance widths in em (unitsPerEm 1000), covering
 * printable ASCII plus every character used in a bundled territory name.
 * Regenerate from the font's hmtx table if the font file changes.
 */
const ZILLA_ITALIC_EM: Record<string, number> = {
  " ": 0.22, "!": 0.237, "\"": 0.366, "#": 0.579, "$": 0.463, "%": 0.675, "&": 0.716, "'": 0.19,
  "(": 0.273, ")": 0.272, "*": 0.407, "+": 0.414, ",": 0.2, "-": 0.342, ".": 0.208, "/": 0.376,
  "0": 0.566, "1": 0.375, "2": 0.505, "3": 0.504, "4": 0.529, "5": 0.503, "6": 0.526, "7": 0.463,
  "8": 0.528, "9": 0.525, ":": 0.22, ";": 0.232, "<": 0.397, "=": 0.565, ">": 0.398, "?": 0.438,
  "@": 0.891, "A": 0.632, "B": 0.588, "C": 0.63, "D": 0.686, "E": 0.573, "F": 0.544, "G": 0.675,
  "H": 0.733, "I": 0.311, "J": 0.442, "K": 0.623, "L": 0.528, "M": 0.856, "N": 0.715, "O": 0.71,
  "P": 0.561, "Q": 0.72, "R": 0.599, "S": 0.523, "T": 0.605, "U": 0.696, "V": 0.632, "W": 0.909,
  "X": 0.616, "Y": 0.597, "Z": 0.563, "[": 0.279, "\\": 0.506, "]": 0.279, "^": 0.445,
  "_": 0.527, "`": 0.431, "a": 0.505, "b": 0.504, "c": 0.44, "d": 0.51, "e": 0.437, "f": 0.306,
  "g": 0.513, "h": 0.505, "i": 0.255, "j": 0.245, "k": 0.446, "l": 0.239, "m": 0.787, "n": 0.523,
  "o": 0.48, "p": 0.52, "q": 0.502, "r": 0.343, "s": 0.418, "t": 0.343, "u": 0.522, "v": 0.469,
  "w": 0.713, "x": 0.477, "y": 0.468, "z": 0.448, "{": 0.277, "|": 0.23, "}": 0.277, "~": 0.603,
  "Î": 0.311, "ô": 0.48, "–": 0.611,
  "ó": 0.481, "ü": 0.522, "ä": 0.504, "ö": 0.481, "Ö": 0.71, "š": 0.418, "Š": 0.523, "ș": 0.418, "é": 0.437
}
/** Wider than any glyph in the table, so an unknown character never under-measures. */
const FALLBACK_EM = 1
/** Leaves room for kerning and rasterisation differences between platforms. */
const WIDTH_SAFETY = 0.94
export const NAME_LINE_HEIGHT_RATIO = 1.15
const STEP = 0.25

export function nameWidthEm(text: string): number {
  let em = 0
  for (const ch of text) em += ZILLA_ITALIC_EM[ch] ?? FALLBACK_EM
  return em
}

/** Greedy word wrap, breaking at spaces and after hyphens, as the native text engines do. */
export function wrapName(text: string, maxWidth: number, fontSize: number): string[] {
  // Split after each hyphen without a lookbehind regex (not safe on every JS engine).
  const words = text.split(' ').flatMap(word => word.replace(/-/g, '-\n').split('\n'))
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const joiner = line && !line.endsWith('-') ? ' ' : ''
    const candidate = line + joiner + word
    if (!line || nameWidthEm(candidate) * fontSize <= maxWidth) line = candidate
    else { lines.push(line); line = word }
  }
  if (line) lines.push(line)
  return lines
}

/**
 * The name as rendered: a zero-width space after each hyphen gives every text
 * engine the same break opportunity wrapName assumes. Android's line breaker
 * otherwise treats "Muhlig-Hofmann" as one word and splits it mid-word.
 */
export function breakableName(name: string): string {
  return name.replace(/-/g, '-\u200B')
}

function fits(text: string, maxWidth: number, maxHeight: number, fontSize: number, maxLines: number): boolean {
  const lines = wrapName(text, maxWidth, fontSize)
  return lines.length <= maxLines
    && lines.length * Math.ceil(fontSize * NAME_LINE_HEIGHT_RATIO) <= maxHeight
    && lines.every(line => nameWidthEm(line) * fontSize <= maxWidth)
}

export interface CardNameFit {
  /** Rendered font size; already includes the system font scale. */
  fontSize: number
  lineHeight: number
  /** True when even the floor size needs more than `maxLines`; the text is ellipsized. */
  truncated: boolean
}

/**
 * Largest size between `min` and `base` (both pre-scale) at which `name` fits
 * `maxLines` lines inside `width` × `height`, scaled by the system `fontScale`.
 * Height matters on short landscape cards, where two full-size lines are
 * taller than the band.
 */
export function fitCardName(name: string, { width, height, base, min, fontScale = 1, maxLines = 2 }: {
  width: number; height: number; base: number; min: number; fontScale?: number; maxLines?: number
}): CardNameFit {
  const maxWidth = width * WIDTH_SAFETY
  const top = base * fontScale
  const floor = min * fontScale
  let size = top
  while (size > floor && !fits(name, maxWidth, height, size, maxLines)) size = Math.max(floor, size - STEP)
  const fontSize = Math.round(size * 100) / 100
  return {
    fontSize,
    lineHeight: Math.ceil(fontSize * NAME_LINE_HEIGHT_RATIO),
    truncated: !fits(name, maxWidth, height, fontSize, maxLines),
  }
}
